import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Image, Plus, Trash2, Edit, Star, StarOff, RefreshCw, 
  Upload, X, Check, FolderOpen, Grid, List, Search,
  ChevronRight, Eye, Camera
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Category colors
const CATEGORY_COLORS = {
  performances: 'bg-primary/20 text-primary',
  events: 'bg-green-500/20 text-green-400',
  parades: 'bg-amber-500/20 text-amber-400',
  classes: 'bg-blue-500/20 text-blue-400',
  workshops: 'bg-purple-500/20 text-purple-400'
};

// Stat Card
const StatCard = ({ icon: Icon, label, value, color = "primary" }) => (
  <div className="bg-stone-800/50 rounded-xl p-4 border border-stone-700">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
        color === 'primary' ? 'bg-primary/20' :
        color === 'green' ? 'bg-green-500/20' :
        color === 'amber' ? 'bg-amber-500/20' :
        'bg-blue-500/20'
      }`}>
        <Icon className={`w-5 h-5 ${
          color === 'primary' ? 'text-primary' :
          color === 'green' ? 'text-green-500' :
          color === 'amber' ? 'text-amber-500' :
          'text-blue-500'
        }`} />
      </div>
      <div>
        <p className="text-2xl font-bold text-white">{value}</p>
        <p className="text-sm text-muted-foreground">{label}</p>
      </div>
    </div>
  </div>
);

// Album Card
const AlbumCard = ({ album, onEdit, onDelete, onToggleFeatured, onView }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-stone-800/50 rounded-xl border border-stone-700 overflow-hidden group"
  >
    {/* Cover Image */}
    <div className="aspect-video relative overflow-hidden">
      {album.cover_image ? (
        <img loading="lazy" decoding="async" 
          src={album.cover_image} 
          alt={album.name}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
      ) : (
        <div className="w-full h-full bg-stone-700 flex items-center justify-center">
          <Image className="w-12 h-12 text-stone-500" />
        </div>
      )}
      
      {/* Featured badge */}
      {album.featured && (
        <div className="absolute top-2 left-2 px-2 py-1 bg-primary text-black text-xs font-bold rounded">
          Featured
        </div>
      )}
      
      {/* Actions overlay */}
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
        <button
          onClick={() => onView(album)}
          className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
          title="View"
        >
          <Eye className="w-5 h-5 text-white" />
        </button>
        <button
          onClick={() => onEdit(album)}
          className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
          title="Edit"
        >
          <Edit className="w-5 h-5 text-white" />
        </button>
        <button
          onClick={() => onToggleFeatured(album)}
          className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
          title={album.featured ? "Remove from featured" : "Add to featured"}
        >
          {album.featured ? (
            <StarOff className="w-5 h-5 text-white" />
          ) : (
            <Star className="w-5 h-5 text-white" />
          )}
        </button>
        <button
          onClick={() => onDelete(album)}
          className="p-2 bg-red-500/50 rounded-lg hover:bg-red-500/70 transition-colors"
          title="Delete"
        >
          <Trash2 className="w-5 h-5 text-white" />
        </button>
      </div>
    </div>
    
    {/* Info */}
    <div className="p-4">
      <div className="flex items-start justify-between mb-2">
        <h3 className="font-bold text-white line-clamp-1">{album.name}</h3>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_COLORS[album.category] || 'bg-stone-600 text-stone-300'}`}>
          {album.category}
        </span>
      </div>
      <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{album.description}</p>
      <p className="text-xs text-muted-foreground flex items-center gap-1">
        <Camera className="w-3 h-3" />
        {album.image_count} photos
      </p>
    </div>
  </motion.div>
);

export default function AdminGallery() {
  const [albums, setAlbums] = useState([]);
  const [stats, setStats] = useState({ total_albums: 0, total_images: 0 });
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddImagesModal, setShowAddImagesModal] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  
  // Forms
  const [albumForm, setAlbumForm] = useState({
    name: '',
    slug: '',
    category: 'events',
    description: '',
    cover_image: '',
    featured: false,
    images: []
  });
  
  const [newImages, setNewImages] = useState('');
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newVideoTitle, setNewVideoTitle] = useState('');
  const [uploadingFiles, setUploadingFiles] = useState(false);

  // Fetch data
  const fetchData = async () => {
    try {
      const [albumsRes, statsRes] = await Promise.all([
        fetch(`${API}/api/gallery/admin/albums`),
        fetch(`${API}/api/gallery/admin/stats`)
      ]);
      
      if (albumsRes.ok) setAlbums(await albumsRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Generate slug from name
  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  // Create album
  const handleCreateAlbum = async () => {
    if (!albumForm.name || !albumForm.slug) {
      toast.error('Name and slug are required');
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/gallery/admin/albums`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(albumForm)
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to create album');
      }
      
      toast.success('Album created!');
      setShowCreateModal(false);
      setAlbumForm({ name: '', slug: '', category: 'events', description: '', cover_image: '', featured: false, images: [] });
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Update album
  const handleUpdateAlbum = async () => {
    if (!selectedAlbum) return;
    
    try {
      const res = await fetch(`${API}/api/gallery/admin/albums/${selectedAlbum.slug}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: albumForm.name,
          category: albumForm.category,
          description: albumForm.description,
          cover_image: albumForm.cover_image,
          featured: albumForm.featured
        })
      });
      
      if (!res.ok) throw new Error('Failed to update album');
      
      toast.success('Album updated!');
      setShowEditModal(false);
      setSelectedAlbum(null);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Delete album
  const handleDeleteAlbum = async (album) => {
    if (!confirm(`Delete "${album.name}"? This cannot be undone.`)) return;
    
    try {
      const res = await fetch(`${API}/api/gallery/admin/albums/${album.slug}`, {
        method: 'DELETE'
      });
      
      if (!res.ok) throw new Error('Failed to delete album');
      
      toast.success('Album deleted');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Toggle featured
  const handleToggleFeatured = async (album) => {
    try {
      const res = await fetch(`${API}/api/gallery/admin/albums/${album.slug}/featured?featured=${!album.featured}`, {
        method: 'PUT'
      });
      
      if (!res.ok) throw new Error('Failed to update');
      
      toast.success(album.featured ? 'Removed from featured' : 'Added to featured');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Upload photo files
  const handleUploadFiles = async (fileList) => {
    if (!selectedAlbum || !fileList || fileList.length === 0) return;
    setUploadingFiles(true);
    try {
      const fd = new FormData();
      Array.from(fileList).forEach(f => fd.append('files', f));
      const res = await fetch(`${API}/api/gallery/admin/albums/${selectedAlbum.slug}/upload`, {
        method: 'POST',
        body: fd
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      toast.success(data.message || 'Photos uploaded');
      setShowAddImagesModal(false);
      setSelectedAlbum(null);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploadingFiles(false);
    }
  };

  // Add video by URL
  const handleAddVideo = async () => {
    if (!selectedAlbum || !newVideoUrl.trim()) {
      toast.error('Please enter a video URL');
      return;
    }
    try {
      const res = await fetch(`${API}/api/gallery/admin/albums/${selectedAlbum.slug}/videos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: newVideoUrl.trim(), title: newVideoTitle.trim() })
      });
      if (!res.ok) throw new Error('Failed to add video');
      toast.success('Video added');
      setNewVideoUrl('');
      setNewVideoTitle('');
      setShowAddImagesModal(false);
      setSelectedAlbum(null);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Add images
  const handleAddImages = async () => {
    if (!selectedAlbum || !newImages.trim()) return;
    
    // Parse URLs (one per line)
    const urls = newImages.split('\n')
      .map(url => url.trim())
      .filter(url => url.startsWith('http'));
    
    if (urls.length === 0) {
      toast.error('Please enter valid image URLs');
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/gallery/admin/albums/${selectedAlbum.slug}/images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ images: urls })
      });
      
      if (!res.ok) throw new Error('Failed to add images');
      
      toast.success(`Added ${urls.length} images`);
      setShowAddImagesModal(false);
      setNewImages('');
      setSelectedAlbum(null);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Open edit modal
  const openEditModal = (album) => {
    setSelectedAlbum(album);
    setAlbumForm({
      name: album.name,
      slug: album.slug,
      category: album.category,
      description: album.description,
      cover_image: album.cover_image,
      featured: album.featured,
      images: []
    });
    setShowEditModal(true);
  };

  // Open add images modal
  const openAddImagesModal = (album) => {
    setSelectedAlbum(album);
    setShowAddImagesModal(true);
  };

  // Filter albums
  const filteredAlbums = albums.filter(album => {
    const matchesSearch = album.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          album.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || album.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-900 text-white p-6" data-testid="admin-gallery">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Photo Gallery Admin</h1>
            <p className="text-muted-foreground">Manage albums and photos</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchData}
              className="p-2 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              New Album
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={FolderOpen} label="Total Albums" value={stats.total_albums} color="primary" />
          <StatCard icon={Image} label="Total Photos" value={stats.total_images?.toLocaleString()} color="green" />
          <StatCard icon={Star} label="Featured" value={albums.filter(a => a.featured).length} color="amber" />
          <StatCard icon={Grid} label="Categories" value={Object.keys(stats.categories || {}).length} color="blue" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search albums..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-stone-800 rounded-lg border border-stone-700 focus:border-primary focus:outline-none"
            />
          </div>
          
          {/* Category filter */}
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 bg-stone-800 rounded-lg border border-stone-700 focus:border-primary focus:outline-none"
          >
            <option value="all">All Categories</option>
            <option value="performances">Performances</option>
            <option value="events">Events</option>
            <option value="parades">Parades</option>
            <option value="classes">Classes</option>
            <option value="workshops">Workshops</option>
          </select>
          
          {/* View mode toggle */}
          <div className="flex bg-stone-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-stone-700' : ''}`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-stone-700' : ''}`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Albums Grid */}
        {viewMode === 'grid' ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredAlbums.map((album) => (
              <AlbumCard
                key={album.slug}
                album={album}
                onEdit={openEditModal}
                onDelete={handleDeleteAlbum}
                onToggleFeatured={handleToggleFeatured}
                onView={openAddImagesModal}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredAlbums.map((album) => (
              <div 
                key={album.slug}
                className="bg-stone-800/50 rounded-lg p-4 border border-stone-700 flex items-center gap-4"
              >
                <div className="w-16 h-16 rounded overflow-hidden flex-shrink-0">
                  {album.cover_image ? (
                    <img loading="lazy" decoding="async" src={album.cover_image} alt={album.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-stone-700 flex items-center justify-center">
                      <Image className="w-6 h-6 text-stone-500" />
                    </div>
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-white truncate">{album.name}</h3>
                  <p className="text-sm text-muted-foreground truncate">{album.description}</p>
                </div>
                
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${CATEGORY_COLORS[album.category]}`}>
                  {album.category}
                </span>
                
                <span className="text-sm text-muted-foreground">{album.image_count} photos</span>
                
                <div className="flex gap-2">
                  <button onClick={() => openEditModal(album)} className="p-2 hover:bg-stone-700 rounded">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={() => openAddImagesModal(album)} className="p-2 hover:bg-stone-700 rounded">
                    <Plus className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDeleteAlbum(album)} className="p-2 hover:bg-red-500/20 rounded text-red-400">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {filteredAlbums.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <FolderOpen className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p>No albums found</p>
          </div>
        )}
      </div>

      {/* Create Album Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-stone-800 rounded-2xl p-6 max-w-md w-full border border-stone-700 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold">Create New Album</h3>
                <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-stone-700 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Album Name *</label>
                  <input
                    type="text"
                    value={albumForm.name}
                    onChange={(e) => {
                      setAlbumForm({ 
                        ...albumForm, 
                        name: e.target.value,
                        slug: generateSlug(e.target.value)
                      });
                    }}
                    placeholder="Zulu Ball 2024"
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Slug *</label>
                  <input
                    type="text"
                    value={albumForm.slug}
                    onChange={(e) => setAlbumForm({ ...albumForm, slug: e.target.value })}
                    placeholder="zulu-ball-2024"
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <select
                    value={albumForm.category}
                    onChange={(e) => setAlbumForm({ ...albumForm, category: e.target.value })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                  >
                    <option value="performances">Performances</option>
                    <option value="events">Events</option>
                    <option value="parades">Parades</option>
                    <option value="classes">Classes</option>
                    <option value="workshops">Workshops</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={albumForm.description}
                    onChange={(e) => setAlbumForm({ ...albumForm, description: e.target.value })}
                    rows={2}
                    placeholder="Photos from the annual Zulu Ball celebration..."
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none resize-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Cover Image URL</label>
                  <input
                    type="text"
                    value={albumForm.cover_image}
                    onChange={(e) => setAlbumForm({ ...albumForm, cover_image: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                  />
                </div>
                
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="featured"
                    checked={albumForm.featured}
                    onChange={(e) => setAlbumForm({ ...albumForm, featured: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label htmlFor="featured" className="text-sm">Featured album</label>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 py-2 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateAlbum}
                    className="flex-1 py-2 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    Create Album
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Album Modal */}
      <AnimatePresence>
        {showEditModal && selectedAlbum && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            onClick={() => setShowEditModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-stone-800 rounded-2xl p-6 max-w-md w-full border border-stone-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold">Edit Album</h3>
                <button onClick={() => setShowEditModal(false)} className="p-1 hover:bg-stone-700 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Album Name</label>
                  <input
                    type="text"
                    value={albumForm.name}
                    onChange={(e) => setAlbumForm({ ...albumForm, name: e.target.value })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <select
                    value={albumForm.category}
                    onChange={(e) => setAlbumForm({ ...albumForm, category: e.target.value })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                  >
                    <option value="performances">Performances</option>
                    <option value="events">Events</option>
                    <option value="parades">Parades</option>
                    <option value="classes">Classes</option>
                    <option value="workshops">Workshops</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={albumForm.description}
                    onChange={(e) => setAlbumForm({ ...albumForm, description: e.target.value })}
                    rows={2}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none resize-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Cover Image URL</label>
                  <input
                    type="text"
                    value={albumForm.cover_image}
                    onChange={(e) => setAlbumForm({ ...albumForm, cover_image: e.target.value })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                  />
                </div>
                
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="edit-featured"
                    checked={albumForm.featured}
                    onChange={(e) => setAlbumForm({ ...albumForm, featured: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label htmlFor="edit-featured" className="text-sm">Featured album</label>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 py-2 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateAlbum}
                    className="flex-1 py-2 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Images Modal */}
      <AnimatePresence>
        {showAddImagesModal && selectedAlbum && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            onClick={() => setShowAddImagesModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-stone-800 rounded-2xl p-6 max-w-lg w-full border border-stone-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold">Add Images</h3>
                  <p className="text-sm text-muted-foreground">to "{selectedAlbum.name}"</p>
                </div>
                <button onClick={() => setShowAddImagesModal(false)} className="p-1 hover:bg-stone-700 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                {/* Upload from device */}
                <div>
                  <label className="block text-sm font-medium mb-1">Upload photos from your device</label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    disabled={uploadingFiles}
                    onChange={(e) => handleUploadFiles(e.target.files)}
                    className="w-full text-sm text-muted-foreground file:mr-3 file:px-4 file:py-2 file:rounded-lg file:border-0 file:bg-primary file:text-black file:font-bold file:cursor-pointer bg-stone-700 rounded-lg border border-stone-600 p-2"
                    data-testid="gallery-upload-input"
                  />
                  {uploadingFiles && <p className="text-xs text-primary mt-1">Uploading...</p>}
                </div>

                {/* Add video */}
                <div className="border-t border-stone-700 pt-4">
                  <label className="block text-sm font-medium mb-1">Add a video (Vimeo / YouTube URL)</label>
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={newVideoUrl}
                      onChange={(e) => setNewVideoUrl(e.target.value)}
                      placeholder="https://vimeo.com/123456789"
                      className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none text-sm"
                      data-testid="gallery-video-url-input"
                    />
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newVideoTitle}
                        onChange={(e) => setNewVideoTitle(e.target.value)}
                        placeholder="Video title (optional)"
                        className="flex-1 px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none text-sm"
                        data-testid="gallery-video-title-input"
                      />
                      <button
                        onClick={handleAddVideo}
                        className="px-4 py-2 bg-primary text-black text-sm font-bold rounded-lg hover:bg-primary/90"
                        data-testid="gallery-add-video-btn"
                      >
                        Add Video
                      </button>
                    </div>
                  </div>
                </div>

                <div className="border-t border-stone-700 pt-4">
                  <label className="block text-sm font-medium mb-1">Image URLs (one per line)</label>
                  <textarea
                    value={newImages}
                    onChange={(e) => setNewImages(e.target.value)}
                    rows={8}
                    placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg&#10;https://example.com/image3.jpg"
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none resize-none font-mono text-sm"
                  />
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Paste image URLs, one per line. Only URLs starting with http:// or https:// will be added.
                </p>
                
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowAddImagesModal(false)}
                    className="flex-1 py-2 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddImages}
                    className="flex-1 py-2 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    Add Images
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
